import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";

/*
 * Copyright (C) 2017 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that they will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */
import React, { Component } from 'react';
import classnames from 'classnames';
import { themeable, ApplyTheme } from '@instructure/ui-themeable';
import { Text, Pill, Avatar } from '@instructure/ui-elements';
import { Checkbox, CheckboxFacade } from '@instructure/ui-forms';
import { ScreenReaderContent, PresentationContent } from '@instructure/ui-a11y';
import { Button } from '@instructure/ui-buttons';
import { IconAssignmentLine, IconQuizLine, IconAnnouncementLine, IconDiscussionLine, IconCalendarMonthLine, IconDocumentLine, IconEditLine, IconPeerReviewLine } from '@instructure/ui-icons';
import NotificationBadge, { MissingIndicator, NewActivityIndicator } from '../NotificationBadge';
import BadgeList from '../BadgeList';
import CalendarEventModal from '../CalendarEventModal';
import responsiviser from '../responsiviser';
const styles = {
  componentId: 'dsRAF',
  template: function template(theme) {
    return "\n\n.root_2eX6{align-items:center;border-bottom:".concat(theme.borderWidth || 'inherit', " solid ").concat(theme.borderColor || 'inherit', ";box-sizing:border-box;color:").concat(theme.color || 'inherit', ";display:flex;flex:1;font-family:").concat(theme.fontFamily || 'inherit', ";line-height:").concat(theme.lineHeight || 'inherit', ";padding:").concat(theme.padding || 'inherit', "}\n\n.root_2eX6.small_2eX6{align-items:flex-start}\n\n.avatar_2eX6,.completed_2eX6,.icon_2eX6,.layout_2eX6{box-sizing:border-box}\n\n.completed_2eX6{-webkit-margin-start:").concat(theme.gutterWidth || 'inherit', ";margin-inline-start:").concat(theme.gutterWidth || 'inherit', ";width:1.375rem}\n\n[dir=ltr] .completed_2eX6{margin-left:").concat(theme.gutterWidth || 'inherit', "}\n\n[dir=rtl] .completed_2eX6{margin-right:").concat(theme.gutterWidth || 'inherit', "}\n\n.activityIndicator_2eX6{-webkit-padding-end:0;-webkit-padding-start:0;padding-inline-end:0;padding-inline-start:0}\n\n[dir=ltr] .activityIndicator_2eX6{padding-left:0;padding-right:0}\n\n[dir=rtl] .activityIndicator_2eX6{padding-left:0;padding-right:0}\n\n.activityIndicator_2eX6+.completed_2eX6{-webkit-margin-start:calc(").concat(theme.gutterWidth || 'inherit', " - ").concat(theme.activityIndicatorWidth || 'inherit', ");margin-inline-start:calc(").concat(theme.gutterWidth || 'inherit', " - ").concat(theme.activityIndicatorWidth || 'inherit', ")}\n\n[dir=ltr] .activityIndicator_2eX6+.completed_2eX6{margin-left:calc(").concat(theme.gutterWidth || 'inherit', " - ").concat(theme.activityIndicatorWidth || 'inherit', ")}\n\n[dir=rtl] .activityIndicator_2eX6+.completed_2eX6{margin-right:calc(").concat(theme.gutterWidth || 'inherit', " - ").concat(theme.activityIndicatorWidth || 'inherit', ")}\n\n.icon_2eX6{color:").concat(theme.iconColor || 'inherit', ";margin:0 ").concat(theme.gutterWidth || 'inherit', "}\n\n.icon_2eX6>svg{display:block}\n\n.avatar_2eX6{margin:0 calc(").concat(theme.gutterWidth || 'inherit', " - (2.5em - ").concat(theme.iconFontSize || 'inherit', ")/2)}\n\n.layout_2eX6{flex-direction:column}\n\n.innerLayout_2eX6,.layout_2eX6{display:flex;flex:1 0;min-width:1px}\n\n.innerLayout_2eX6{align-items:center;min-height:2.5rem}\n\n.details_2eX6{box-sizing:border-box;flex:0 0 50%;margin-bottom:0;min-width:1px}\n\n.details_2eX6.details_no_badges_2eX6{flex:0 0 75%}\n\n.secondary_2eX6{align-items:center;box-sizing:border-box;display:flex;flex:0 0 50%;justify-content:flex-end;min-width:1px}\n\n.secondary_2eX6.secondary_no_badges_2eX6{flex:0 0 25%}\n\n.type_2eX6{letter-spacing:0.0625rem;line-height:1;margin-bottom:").concat(theme.typeMargin || 'inherit', ";text-transform:uppercase}\n\n.title_2eX6,.type_2eX6{box-sizing:border-box}\n\n.title_2eX6{line-height:").concat(theme.titleLineHeight || 'inherit', "}\n\n.metrics_2eX6{-webkit-padding-start:").concat(theme.metricsPadding || 'inherit', ";box-sizing:border-box;flex:0 0 10rem;min-width:1px;padding-inline-start:").concat(theme.metricsPadding || 'inherit', ";text-align:end}\n\n[dir=ltr] .metrics_2eX6{padding-left:").concat(theme.metricsPadding || 'inherit', ";text-align:right}\n\n[dir=rtl] .metrics_2eX6{padding-right:").concat(theme.metricsPadding || 'inherit', ";text-align:left}\n\n.metrics_2eX6.with_end_time_2eX6{flex-basis:14rem}\n\n.metrics_2eX6.with_end_time_2eX6 .due_2eX6{text-transform:none}\n\n.due_2eX6,.score_2eX6{box-sizing:border-box;letter-spacing:0.0625rem;line-height:1;text-transform:uppercase;white-space:nowrap}\n\n.badges_2eX6{flex:1;min-width:1px;text-align:end}\n\n[dir=ltr] .badges_2eX6{text-align:right}\n\n[dir=rtl] .badges_2eX6{text-align:left}\n\n.feedback_2eX6{align-items:center;display:flex;min-height:40px}\n\n.feedback_2eX6 .feedbackAvatar_2eX6{-webkit-margin-end:").concat(theme.gutterWidth || 'inherit', ";flex-shrink:0;margin-inline-end:").concat(theme.gutterWidth || 'inherit', "}\n\n[dir=ltr] .feedback_2eX6 .feedbackAvatar_2eX6{margin-right:").concat(theme.gutterWidth || 'inherit', "}\n\n[dir=rtl] .feedback_2eX6 .feedbackAvatar_2eX6{margin-left:").concat(theme.gutterWidth || 'inherit', "}\n\n.feedback_2eX6 .feedbackComment_2eX6,.medium_2eX6 .title_2eX6,.small_2eX6 .title_2eX6{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}\n\n.medium_2eX6 .title_2eX6,.small_2eX6 .title_2eX6{-webkit-padding-end:0.5rem;padding-inline-end:0.5rem}\n\n[dir=ltr] .medium_2eX6 .title_2eX6,[dir=ltr] .small_2eX6 .title_2eX6{padding-right:0.5rem}\n\n[dir=rtl] .medium_2eX6 .title_2eX6,[dir=rtl] .small_2eX6 .title_2eX6{padding-left:0.5rem}\n\n.small_2eX6{padding-left:0;padding-right:0}\n\n.small_2eX6 .completed_2eX6{-webkit-margin-start:6px;margin-inline-start:6px}\n\n[dir=ltr] .small_2eX6 .completed_2eX6{margin-left:6px}\n\n[dir=rtl] .small_2eX6 .completed_2eX6{margin-right:6px}\n\n.small_2eX6 .innerLayout_2eX6{-webkit-margin-start:1rem;align-items:flex-start;flex-direction:column;margin-inline-start:1rem}\n\n[dir=ltr] .small_2eX6 .innerLayout_2eX6{margin-left:1rem}\n\n[dir=rtl] .small_2eX6 .innerLayout_2eX6{margin-right:1rem}\n\n.small_2eX6 .details_2eX6{margin-bottom:1rem}\n\n.small_2eX6 .details_2eX6,.small_2eX6 .secondary_2eX6{flex:1 0 auto;width:100%}\n\n.small_2eX6 .metrics_2eX6{-webkit-padding-start:0;align-items:flex-end;display:flex;flex:1 0 auto;flex-direction:row-reverse;justify-content:space-between;padding-inline-start:0;text-align:unset}\n\n[dir=ltr] .small_2eX6 .metrics_2eX6{padding-left:0;text-align:unset}\n\n[dir=rtl] .small_2eX6 .metrics_2eX6{padding-right:0;text-align:unset}\n\n.small_2eX6 .due_2eX6{width:100%}\n\n.small_2eX6 .avatar_2eX6,.small_2eX6 .badges_2eX6,.small_2eX6 .editButton_2eX6,.small_2eX6 .feedback_2eX6,.small_2eX6 .icon_2eX6{display:none}\n\n.small_2eX6 .location_2eX6{-webkit-margin-start:1rem;margin-inline-start:1rem}\n\n[dir=ltr] .small_2eX6 .location_2eX6{margin-left:1rem}\n\n[dir=rtl] .small_2eX6 .location_2eX6{margin-right:1rem}");
  },
  'root': 'root_2eX6',
  'small': 'small_2eX6',
  'avatar': 'avatar_2eX6',
  'completed': 'completed_2eX6',
  'icon': 'icon_2eX6',
  'layout': 'layout_2eX6',
  'activityIndicator': 'activityIndicator_2eX6',
  'innerLayout': 'innerLayout_2eX6',
  'details': 'details_2eX6',
  'details_no_badges': 'details_no_badges_2eX6',
  'secondary': 'secondary_2eX6',
  'secondary_no_badges': 'secondary_no_badges_2eX6',
  'type': 'type_2eX6',
  'title': 'title_2eX6',
  'metrics': 'metrics_2eX6',
  'with_end_time': 'with_end_time_2eX6',
  'due': 'due_2eX6',
  'score': 'score_2eX6',
  'badges': 'badges_2eX6',
  'feedback': 'feedback_2eX6',
  'feedbackAvatar': 'feedbackAvatar_2eX6',
  'feedbackComment': 'feedbackComment_2eX6',
  'medium': 'medium_2eX6',
  'editButton': 'editButton_2eX6',
  'location': 'location_2eX6'
};
import theme from './theme.js';
import { arrayOf, bool, number, string, func, shape, object } from 'prop-types';
import { badgeShape, userShape, statusShape, sizeShape, feedbackShape } from '../plannerPropTypes';
import { showPillForOverdueStatus } from '../../utilities/statusUtils';
import { momentObj } from 'react-moment-proptypes';
import formatMessage from '../../format-message';
import { animatable } from '../../dynamic-ui';
export class PlannerItem extends Component {
  constructor(props) {
    super(props);

    this.toDoLinkClick = e => {
      e.preventDefault();
      this.props.updateTodo({
        updateTodoItem: _objectSpread({}, this.props)
      });
    };

    this.registerRootDivRef = elt => {
      this.rootDivRef = elt;
    };

    this.registerFocusElementRef = elt => {
      this.checkboxRef = elt;
    };

    this.getFocusable = which => {
      return which === 'update' || which === 'delete' ? this.itemLink : this.checkboxRef;
    };

    this.renderDateField = () => {
      if (this.props.date) {
        if (this.props.allDay) {
          return formatMessage('All Day');
        }

        if (this.props.associated_item === "Calendar Event") {
          if (this.showEndTime()) {
            return formatMessage("{startTime} to {endTime}", {
              startTime: this.props.date.format("LT"),
              endTime: this.props.endTime.format("LT")
            });
          } else {
            return formatMessage(this.props.date.format("LT"));
          }
        }

        if (this.hasDueTime()) {
          if (this.props.associated_item === "Peer Review") {
            return formatMessage("Reminder: {date}", {
              date: this.props.date.format("LT")
            });
          } else if (this.props.dateStyle === 'todo') {
            return formatMessage("To Do: {date}", {
              date: this.props.date.format("LT")
            });
          } else {
            return formatMessage("Due: {date}", {
              date: this.props.date.format("LT")
            });
          }
        }

        return this.props.date.format("LT");
      }

      return null;
    };

    this.openCalendarEventModal = () => {
      this.setState({
        calendarEventModalOpen: true
      });
    };

    this.closeCalendarEventModal = () => {
      this.setState({
        calendarEventModalOpen: false
      });
    };

    this.renderIcon = () => {
      const currentUser = this.props.currentUser || {};

      switch (this.props.associated_item) {
        case "Assignment":
          return React.createElement(IconAssignmentLine, null);

        case "Quiz":
          return React.createElement(IconQuizLine, null);

        case "Discussion":
          return React.createElement(IconDiscussionLine, null);

        case "Announcement":
          return React.createElement(IconAnnouncementLine, null);

        case "Calendar Event":
          return React.createElement(IconCalendarMonthLine, null);

        case "Page":
          return React.createElement(IconDocumentLine, null);

        case "Peer Review":
          return React.createElement(IconPeerReviewLine, null);

        default:
          return React.createElement(Avatar, {
            name: currentUser.displayName || '?',
            src: currentUser.avatarUrl,
            size: "small"
          });
      }
    };

    this.renderBadges = () => {
      if (this.props.badges.length) {
        return React.createElement(BadgeList, null, this.props.badges.map(b => React.createElement(Pill, {
          key: b.id,
          text: b.text,
          variant: b.variant
        })));
      }

      return null;
    };

    this.renderItemSubMetric = () => {
      if (this.props.points) {
        return React.createElement("div", {
          className: styles.score
        }, React.createElement(Text, {
          color: "secondary"
        }, React.createElement(Text, {
          size: "large"
        }, this.props.points), React.createElement(Text, {
          size: "x-small"
        }, "\xA0", formatMessage('pts'))));
      }

      if (this.props.associated_item === 'To Do') {
        return React.createElement("div", {
          className: styles.editButton
        }, React.createElement(ApplyTheme, {
          theme: {
            [Button.theme]: {
              iconColor: this.props.color
            }
          }
        }, React.createElement(Button, {
          variant: "icon",
          icon: IconEditLine,
          onClick: this.toDoLinkClick
        }, React.createElement(ScreenReaderContent, null, formatMessage('Edit')))));
      }

      return null;
    };

    this.renderItemMetrics = () => {
      const secondaryClasses = classnames(styles.secondary, !this.hasBadges() ? styles.secondary_no_badges : '');
      const metricsClasses = classnames(styles.metrics, {
        [styles.with_end_time]: this.showEndTime()
      });
      return React.createElement("div", {
        className: secondaryClasses
      }, React.createElement("div", {
        className: styles.badges
      }, this.renderBadges()), React.createElement("div", {
        className: metricsClasses
      }, this.renderItemSubMetric(), React.createElement("div", {
        className: styles.due
      }, React.createElement(Text, {
        color: "secondary",
        size: "x-small"
      }, React.createElement(PresentationContent, null, this.renderDateField())))));
    };

    this.renderType = () => {
      if (!this.props.associated_item) {
        return formatMessage('{course} TO DO', {
          course: this.props.courseName || ''
        });
      } else {
        return "".concat(this.props.courseName || '', " ").concat(this.assignmentType());
      }
    };

    this.renderItemDetails = () => {
      return React.createElement("div", {
        className: classnames(styles.details, !this.hasBadges() ? styles.details_no_badges : '')
      }, React.createElement("div", {
        className: styles.type
      }, React.createElement(Text, {
        size: "x-small",
        color: "secondary"
      }, this.renderType())), this.renderTitle());
    };

    this.getCheckboxTheme = () => {
      return {
        checkedBackground: this.props.color,
        checkedBorderColor: this.props.color,
        borderColor: this.props.color,
        hoverBorderColor: this.props.color
      };
    };

    this.state = {
      calendarEventModalOpen: false,
      completed: props.completed
    };
  }

  componentDidMount() {
    this.props.registerAnimatable('item', this, this.props.animatableIndex, [this.props.uniqueId]);
  }

  componentWillReceiveProps(nextProps) {
    this.props.deregisterAnimatable('item', this, [this.props.uniqueId]);
    this.props.registerAnimatable('item', this, nextProps.animatableIndex, [nextProps.uniqueId]);
    this.setState({
      completed: nextProps.completed
    });
  }

  componentWillUnmount() {
    this.props.deregisterAnimatable('item', this, [this.props.uniqueId]);
  }

  getScrollable() {
    return this.rootDivRef;
  }

  getLayout() {
    return this.props.responsiveSize;
  }

  hasDueTime() {
    return this.props.date && !(this.props.associated_item === "Announcement" || this.props.associated_item === "Calendar Event");
  }

  showEndTime() {
    return this.props.date && !this.props.allDay && this.props.endTime && !this.props.endTime.isSame(this.props.date);
  }

  hasBadges() {
    return this.props.badges && this.props.badges.length && this.props.badges.length > 0;
  }

  assignmentType() {
    switch (this.props.associated_item) {
      case 'Quiz':
        return formatMessage('Quiz');

      case 'Discussion':
        return formatMessage('Discussion');

      case 'Assignment':
        return formatMessage('Assignment');

      case 'Page':
        return formatMessage('Page');

      case 'Announcement':
        return formatMessage('Announcement');

      case 'To Do':
        return formatMessage('To Do');

      case 'Calendar Event':
        return formatMessage('Calendar Event');

      case 'Peer Review':
        return formatMessage('Peer Review');

      default:
        return formatMessage('Task');
    }
  }

  linkLabel() {
    const assignmentType = this.assignmentType();
    const datetimeformat = this.props.allDay === true ? 'LL' : 'LLLL';
    const params = {
      assignmentType,
      title: this.props.title,
      datetime: this.props.date ? this.props.date.format(datetimeformat) : null
    };

    if (this.props.date) {
      if (this.props.allDay) {
        return formatMessage('{assignmentType} {title}, all day on {datetime}.', params);
      }

      if (this.props.associated_item === "Calendar Event") {
        if (this.showEndTime()) {
          params.endTime = this.props.endTime.format('LT');
          return formatMessage('{assignmentType} {title}, at {datetime} until {endTime}', params);
        } else {
          return formatMessage('{assignmentType} {title}, at {datetime}.', params);
        }
      }

      if (this.hasDueTime()) {
        if (this.props.dateStyle === 'todo') {
          return formatMessage('{assignmentType} {title} has a to do time at {datetime}.', params);
        } else if (this.props.associated_item === 'Peer Review') {
          return formatMessage('{assignmentType} {title}, reminder {datetime}.', params);
        } else {
          return formatMessage('{assignmentType} {title}, due {datetime}.', params);
        }
      }

      if (this.props.associated_item === "Announcement") {
        return formatMessage('{assignmentType} {title} posted {datetime}.', params);
      }
    }

    return formatMessage('{assignmentType} {title}.', params);
  }

  renderCalendarEventModal() {
    if (this.props.associated_item !== 'Calendar Event') return null;
    return React.createElement(CalendarEventModal, {
      open: this.state.calendarEventModalOpen,
      requestClose: this.closeCalendarEventModal,
      title: this.props.title,
      html_url: this.props.html_url,
      courseName: this.props.courseName,
      currentUser: this.props.currentUser,
      location: this.props.location,
      address: this.props.address,
      details: this.props.details,
      startTime: this.props.date,
      endTime: this.props.endTime,
      allDay: !!this.props.allDay,
      timeZone: this.props.timeZone
    });
  }

  renderTitle() {
    const linkProps = {};

    if (this.props.associated_item === 'To Do') {
      linkProps.onClick = this.toDoLinkClick;
    }

    if (this.props.associated_item === 'Calendar Event') {
      linkProps.onClick = this.openCalendarEventModal;
    } else {
      linkProps.href = this.props.html_url;
    }

    return React.createElement("div", {
      className: styles.title,
      style: {
        position: 'relative'
      }
    }, React.createElement(Button, Object.assign({
      variant: "link",
      theme: {
        mediumPadding: '0',
        mediumHeight: 'normal'
      },
      buttonRef: link => {
        this.itemLink = link;
      }
    }, linkProps), React.createElement(ScreenReaderContent, null, this.linkLabel()), React.createElement(PresentationContent, null, this.props.title), this.renderCalendarEventModal()));
  }

  renderNotificationBadge() {
    if (!this.props.showNotificationBadge) {
      return null;
    }

    const newItem = this.props.newActivity;
    let missing = false;

    if (showPillForOverdueStatus('missing', {
      status: this.props.status,
      context: this.props.context
    })) {
      missing = true;
    }

    if (newItem || missing) {
      const IndicatorComponent = newItem ? NewActivityIndicator : MissingIndicator;
      return React.createElement(NotificationBadge, {
        responsiveSize: this.props.responsiveSize
      }, React.createElement("div", {
        className: styles.activityIndicator
      }, React.createElement(IndicatorComponent, {
        title: this.props.title,
        itemIds: [this.props.uniqueId],
        animatableIndex: this.props.animatableIndex,
        getFocusable: this.getFocusable
      })));
    } else {
      return React.createElement(NotificationBadge, {
        responsiveSize: this.props.responsiveSize
      });
    }
  }

  renderExtraInfo() {
    const feedback = this.props.feedback;

    if (feedback) {
      const comment = feedback.is_media ? formatMessage("You have media feedback.") : feedback.comment;
      return React.createElement("div", {
        className: styles.feedback
      }, React.createElement("span", {
        className: styles.feedbackAvatar
      }, React.createElement(Avatar, {
        name: feedback.author_name || '?',
        src: feedback.author_avatar_url,
        size: "small"
      })), React.createElement("span", {
        className: styles.feedbackComment
      }, React.createElement(Text, {
        fontStyle: "italic"
      }, comment)));
    }

    const location = this.props.location;

    if (location) {
      return React.createElement("div", {
        className: styles.location
      }, React.createElement(Text, {
        color: "secondary"
      }, location));
    }

    return null;
  }

  render() {
    const assignmentType = this.assignmentType();
    const checkboxLabel = this.state.completed ? formatMessage('{assignmentType} {title} is marked as done.', {
      assignmentType: assignmentType,
      title: this.props.title
    }) : formatMessage('{assignmentType} {title} is not marked as done.', {
      assignmentType: assignmentType,
      title: this.props.title
    });
    return React.createElement("div", {
      className: classnames(styles.root, styles[this.getLayout()], 'planner-item'),
      ref: this.registerRootDivRef
    }, this.renderNotificationBadge(), React.createElement("div", {
      className: styles.completed
    }, React.createElement(ApplyTheme, {
      theme: {
        [CheckboxFacade.theme]: this.getCheckboxTheme()
      }
    }, React.createElement(Checkbox, {
      ref: this.registerFocusElementRef,
      label: React.createElement(ScreenReaderContent, null, checkboxLabel),
      checked: this.props.toggleAPIPending ? !this.state.completed : this.state.completed,
      onChange: this.props.toggleCompletion,
      disabled: this.props.toggleAPIPending
    }))), React.createElement("div", {
      className: this.props.associated_item === 'To Do' ? styles.avatar : styles.icon,
      style: {
        color: this.props.color
      },
      "aria-hidden": "true"
    }, this.renderIcon()), React.createElement("div", {
      className: styles.layout
    }, React.createElement("div", {
      className: styles.innerLayout
    }, this.renderItemDetails(), this.renderItemMetrics()), this.renderExtraInfo()));
  }

}
PlannerItem.propTypes = {
  color: string,
  id: string.isRequired,
  uniqueId: string.isRequired,
  animatableIndex: number,
  title: string.isRequired,
  points: number,
  date: momentObj,
  dateStyle: string,
  details: string,
  courseName: string,
  completed: bool,
  overrideId: string,
  associated_item: string,
  context: object,
  html_url: string,
  toggleCompletion: func,
  updateTodo: func.isRequired,
  badges: arrayOf(shape(badgeShape)),
  registerAnimatable: func,
  deregisterAnimatable: func,
  toggleAPIPending: bool,
  status: statusShape,
  newActivity: bool,
  showNotificationBadge: bool,
  currentUser: shape(userShape),
  responsiveSize: sizeShape,
  allDay: bool,
  feedback: shape(feedbackShape),
  location: string,
  endTime: momentObj,
  timeZone: string.isRequired
};
PlannerItem.defaultProps = {
  badges: [],
  responsiveSize: 'large',
  allDay: false
};
const ResponsivePlannerItem = responsiviser()(PlannerItem);
export default animatable(themeable(theme, styles)(ResponsivePlannerItem));